import { FC, useEffect, useState } from "react";
import { useAuthStore } from "../../hooks/useAuthStore";
import { useTicketStore } from "../../hooks/useTicketStore";
import { Button, Grid } from "@mui/material";
import CustomTable from "../../components/common/CustomTable/CustomTable";
import { ApiStatus } from "../../types/api/status";
import { Ticket } from "../../types/slices/ticketType";
import { RoutesMap } from "../../types";
import { ModalConfirm } from "../../components/common/ModalConfirm/ModalConfirm";
import { moneyCL } from "../../toolbox/helpers/money-helpers";
import { formatDate } from "../../toolbox/helpers/formatDateHelper";
import { SaleDetailDrawer } from "./components/drawers/SaleDetailDrawer";
import { useSupplierStore } from "../../hooks/useSupplierStore";
import { Supplier } from "../../types/slices/supplierType";

const columns = [
    { type: "checkbox", field: "", label: "", align: "center" },
    { type: 'text', field: 'date', label: 'Fecha', align: 'center', format: (row) => formatDate(row.date) },
    { type: 'text', field: 'folio', label: 'Folio', align: 'center' },
    { type: 'text', field: 'name_customer', label: 'Cliente', align: 'center' },
    { type: 'text', field: 'type_document', label: 'Tipo', align: 'center' },
    { type: 'text', field: 'net_total', label: 'Total neto', align: 'right', format: (row) => moneyCL(row.net_total || 0) },
    { type: 'text', field: 'total_discount', label: 'Descuento', align: 'right', format: (row) => moneyCL(row.total_discount || 0) },
    { type: 'text', field: 'IVA', label: 'IVA', align: 'right', format: (row) => moneyCL(row.IVA || 0) },
    { type: 'text', field: 'total', label: 'Total', align: 'right', format: (row) => moneyCL(row.total || 0) },
    { type: 'detail', field: 'detail', label: 'Detalle', align: 'center' },
    // { type: 'options', field: 'options', label: 'Opciones', align: 'center' },
]

const breadcrumbs = [
    { id: 1, label: 'Contabilidad' },
    { id: 2, label: 'Ventas', pathname: RoutesMap.SALES_LIST }
]

interface onList {
    page?: number
    perPage?: number
    search?: string
    start_date?: string
    end_date?: string
    types_document: number[]
    status_invoice_route: string[]
    idcustomer?: number
}

const today = new Date().toISOString().split('T')[0]

export const SalesList: FC = () => {

    const { user } = useAuthStore()
    const { status, tickets, totalRows, getTicket, deleteTicket, createRelationTicket } = useTicketStore()
    const { status: statusSupplier, supppliers, getSuppliers } = useSupplierStore()

    const [selectedOrders, setSelectedOrders] = useState<number[]>([]);
    const [allSelected, setAllSelected] = useState<boolean>(false);
    const [rowSelected, setRowSelected] = useState<any>('');
    const [showModalConfirm, setShowModalConfirm] = useState<boolean>(false);
    const [loading, setLoading] = useState(false);
    const [showDrawer, setShowDrawer] = useState<string>('')
    const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null)

    const [search, setSearch] = useState<string>('')
    const [startDate, setStartDate] = useState<string>(today)
    const [endDate, setEndDate] = useState<string>(today)
    const [page, setPage] = useState<number>(1)
    const [perPage, setPerPage] = useState<number>(10)

    useEffect(() => {
        getSuppliers()
    }, [])

    useEffect(() => {
        if (search === '') {
            getTicketApi()
        } else {
            const timer = setTimeout(() => getTicketApi(), 500)
            return () => clearTimeout(timer)
        }
    }, [page, perPage, search, startDate, endDate, selectedSupplier])

    useEffect(() => {
        if (rowSelected?.id && tickets.length > 0) {
            const ticket = tickets.find(row => row.id === rowSelected?.id)
            setRowSelected(ticket || null)
        }
    }, [rowSelected, tickets])

    const getTicketApi = async () => {
        const payload: onList = {
            status_invoice_route: ['1', '2'],
            types_document: [1, 2, 7],
            idcustomer: selectedSupplier?.id
        }
        if (search !== '') payload.search = search
        if (startDate !== '') payload.start_date = startDate
        if (endDate !== '') payload.end_date = endDate

        await getTicket(payload)
    }

    const onDetails = (rowSelected: Ticket) => {
        setRowSelected(rowSelected)
        setShowDrawer('detail')
    }

    const isSelected = (row: any) => selectedOrders.some(selectedRow => selectedRow === row.id);

    const selectAllRows = () => {
        if (allSelected) {
            setSelectedOrders([]);
            setAllSelected(false);
        } else {
            const allIds = tickets.map((ticket) => ticket.id);
            setSelectedOrders(allIds);
            setAllSelected(true);
        }
    };

    const handleSelectOrder = (row) => {
        setSelectedOrders((prev) =>
            prev.includes(row.id) ? prev.filter((orderId) => orderId !== row.id) : [...prev, row.id]
        );
    };

    const canGroupOrders = () => {
        if (selectedOrders.length < 2) return false;

        const selectedPedidos = tickets.filter(pedido => selectedOrders.includes(pedido.id));

        if (!selectedPedidos.every(p => p.idtype_document === 7)) {
            return false;
        }

        const firstPedido = selectedPedidos[0];
        return selectedPedidos.every(p =>
            p.idcustomer === firstPedido.idcustomer &&
            p.idclient_address === firstPedido.idclient_address &&
            p.idcompany === firstPedido.idcompany &&
            p.idway_to_pay === firstPedido.idway_to_pay &&
            p.bussines_area === firstPedido.bussines_area
        );
    };

    const handleGroupOrders = async () => {
        const groupedOrderIds = selectedOrders;
        await createRelationTicket({ idtickets_child: groupedOrderIds })
        await getTicketApi()
        setSelectedOrders([]);
        setAllSelected(false);
    };

    const onDelete = async (row) => {
        setRowSelected(row);
        setShowModalConfirm(true);
    }

    const clearState = () => {
        setRowSelected('');
    }

    const onDeleteConfirm = async () => {
        if (rowSelected) {
            setLoading(true);
            await deleteTicket(rowSelected.id)
            await getTicketApi()
            setLoading(false);
            setShowModalConfirm(false);
        }
    }

    const onFilterStartDate = (date) => {
        setStartDate(date)
    }

    const onFilterEndDate = (date) => {
        setEndDate(date)
    }

    const handleChangePage = (event: any, newPage: any) => {
        setPage(newPage + 1)
    }

    const handleChangeRowsPerPage = (event) => {
        setPerPage(+event.target.value)
        setPage(1)
    }

    const onSearch = (search) => {
        setSearch(search)
    }

    const onFilter1 = (option) => {
        setSelectedSupplier(option)
    }

    const optionLabel1 = (option) => {
        return option.name_rz + ' - ' + option.rut
    }

    return (
        <>
            <Grid item xs={12}>
                <CustomTable
                    breadcrumbs={breadcrumbs}
                    title={'Mis ventas'}
                    columns={columns}
                    rows={tickets || []}
                    loading={status === ApiStatus.FETCHING}
                    onRowClick={() => { }}
                    isSelected={isSelected}
                    selectAll={selectAllRows}
                    allSelected={allSelected}
                    onCheckbox={handleSelectOrder}
                    hasOptions
                    onDetails={onDetails}
                    onAddFn={handleGroupOrders}
                    disabledAdd={!canGroupOrders()}
                    onAddFnName={'Agrupar Seleccionados'}
                    onSearch={onSearch}
                    // onDelete={onDelete}

                    startDate={startDate}
                    endDate={endDate}
                    onFilterStartDate={onFilterStartDate}
                    onFilterEndDate={onFilterEndDate}

                    totalRows={totalRows}
                    rowsPerPage={perPage}
                    page={page}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    withCustomPagination
                    dense

                    filter1={selectedSupplier}
                    optionsFilter1={supppliers}
                    optionLabel1={optionLabel1}
                    labelFilter1="Cliente"
                    onFilter1={onFilter1}
                />
            </Grid>
            {
                showModalConfirm && (
                    <ModalConfirm
                        open={showModalConfirm}
                        closeModal={() => { setShowModalConfirm(false); clearState() }}
                        onConfirm={onDeleteConfirm}
                        status={loading}
                    />
                )
            }
            {showDrawer === 'detail' && (
                <SaleDetailDrawer
                    open={showDrawer === 'detail'}
                    onClose={() => setShowDrawer('')}
                    ticket={rowSelected}
                    onDeleteConfirm={onDeleteConfirm}
                    loading={loading}
                    getTicketApi={getTicketApi}
                />
            )}
        </>

    )
}